import ValidationStatus from '../Data/ValidationStatus';

export const getComponentValidationMapping = (countryCode, component) => {
  const componentValidationMapping = {};

  if (!component?.validation || !component?.validation.constraints) {
    return componentValidationMapping;
  }

  Object.keys(component.validation.constraints).forEach((constraint) => {
    let constraintIsValid = true;

    if (constraint === 'date') {
      constraintIsValid = isFilled(component);
    }
    if (constraint === 'required') {
      constraintIsValid = isFilled(component);
    }
    if (constraint === 'email') {
      constraintIsValid = emailValid(component);
    }
    if (constraint === 'addressWithHouseNumber') {
      const addressDetails = component?.input?.addressDetails;
      constraintIsValid = !!(addressDetails && addressDetails.streetNumber);
      // Fallback validation: if addressDetails are not present, check if value contains a number
      if (!constraintIsValid && !addressDetails && isFilled(component)) {
        constraintIsValid = stringContainsNumber(component.input.value);
      }
    }
    if (constraint === 'other_option_required') {
      constraintIsValid = isOtherInputFilled(component);
      if (isFilled(component)) {
        // if other options have been selected, then other input is not required
        constraintIsValid = true;
      }
    }
    if (
      isFilled(component) &&
      (constraint === 'phone_work' ||
        constraint === 'phone_mobile' ||
        constraint === 'phoneInternational' ||
        constraint === 'phone' ||
        constraint === 'gbpingphone' ||
        constraint === 'telephone' ||
        constraint === 'postcode')
    ) {
      constraintIsValid =
        component.validation.constraints[constraint].status ===
          ValidationStatus.VALID && component.input.value !== '';
    }

    if (constraint === 'postcodeRegex' && isFilled(component)) {
      const { value } = component.input;
      constraintIsValid = isPostcodeValidRegex(countryCode, value);
    }
    if (constraint === 'fullname' && isFilled(component)) {
      constraintIsValid = isFullNameValidRegex(component);
    }
    if (constraint === 'numbersOnly') {
      constraintIsValid = isNumbersFormatCorrect(component);
    }
    componentValidationMapping[constraint] = constraintIsValid;
  });

  return componentValidationMapping;
};

const isFilled = (component) =>
  component.input.type === 'hidden' ||
  (component.input.value !== undefined && component.input.value !== '');

const isPostcodeValidRegex = (countryCode, value) => {
  const postcodeRegexCountries = {
    GB: /(GIR 0AA)|(((A[BL]|B[ABDHLNRSTX]?|C[ABFHMORTVW]|D[ADEGHLNTY]|E[HNX]?|F[KY]|G[LUY]?|H[ADGPRSUX]|I[GMPV]|JE|K[ATWY]|L[ADELNSU]?|M[EKL]?|N[EGNPRW]?|O[LX]|P[AEHLOR]|R[GHM]|S[AEGKLMNOPRSTY]?|T[ADFNQRSW]|UB|W[ADFNRSV]|YO|ZE)[1-9]?[0-9]|((E|N|NW|SE|SW|W)1|EC[1-4]|WC[12])[A-HJKMNPR-Y]|(SW|W)([2-9]|[1-9][0-9])|EC[1-9][0-9]) ?[0-9][ABD-HJLNP-UW-Z]{2})/,
    US: /^[0-9]{5}([- /]?[0-9]{4})?$/,
    CA: /^[a-vx-yA-VX-Y]\d{1}[a-zA-Z](-| |)\d{1}[a-zA-Z]\d{1}$/,
    AU: /^[0-9]{4}$/,
    DE: /^(?!01000|99999)(0[1-9]\d{3}|[1-9]\d{4})$/,
  };

  if (!postcodeRegexCountries[countryCode]) {
    return true; // to avoid blocking navigation if the the country code is not provided above allow any postcode
  }

  return postcodeRegexCountries[countryCode].test(value.toUpperCase().trim());
};

export const isFullNameValidRegex = (component) => {
  const { value } = component.input;
  const regexFullName = /[^]\s[^]/;
  return regexFullName.test(value.toUpperCase().trim());
};

const isNonEmptyNumberFilledString = (testString) =>
  Boolean(testString && /^\d+$/.test(testString));

const meetsMinimumValue = (minValue, inputValue) =>
  !isNonEmptyNumberFilledString(minValue) ||
  parseInt(inputValue, 10) >= parseInt(minValue, 10);

const meetsMinimumDigits = (minDigits, inputValue) =>
  !isNonEmptyNumberFilledString(minDigits) ||
  inputValue.length >= parseInt(minDigits, 10);

const meetsMaximumDigits = (maxDigits, inputValue) =>
  !isNonEmptyNumberFilledString(maxDigits) ||
  inputValue.length <= parseInt(maxDigits, 10);

export const isNumbersFormatCorrect = (component) => {
  const value = component.input?.value;
  if (!value) {
    return false;
  }
  const formattedValue = value.trim().replace(/-|\s/g, '');
  const numbersOnlyConstraint = component.validation.constraints.numbersOnly;

  return (
    isNonEmptyNumberFilledString(formattedValue) &&
    meetsMinimumValue(numbersOnlyConstraint.min_number, formattedValue) &&
    meetsMinimumDigits(numbersOnlyConstraint.min_length, formattedValue) &&
    meetsMaximumDigits(numbersOnlyConstraint.max_length, formattedValue)
  );
};

export const emailValid = (component) => {
  const emailValue = component.input.value;

  // Taken from https://emailregex.com/index.html to conform to RFC 5322.
  const regexEmail =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return !!(emailValue && !!emailValue.match(regexEmail));
};

const isOtherInputFilled = (component) => {
  if (component.component.type === 'radios') {
    return component.input.value !== '';
  }
  // TODO refactor this, this code is not clear and a function should be used to clarify what it does.
  return !!(
    component.input.selectedOptions &&
    (!component.input.selectedOptions.includes('other-option') ||
      component.input.otherValue)
  );
};

const stringContainsNumber = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  return /\d/.test(value.trim());
};

export const getErrorMessage = (constraints) => {
  if (constraints) {
    // eslint-disable-next-line no-restricted-syntax
    for (const constraint of Object.values(constraints)) {
      if (constraint.status === ValidationStatus.INVALID) {
        return constraint.message;
      }
    }
  }
};
